import {omneticApi} from '@dms/api/base';

import {getContentDispositionFileName} from 'shared/web';

import {
  ExportSalesLeagueBranchBoardCsvApiArg,
  ExportSalesLeagueBranchBoardCsvApiResponse,
  ExportSalesLeagueLeaderboardCsvApiArg,
  ExportSalesLeagueLeaderboardCsvApiResponse,
  exportSalesLeagueBranchBoardCsvApiArgSchema,
  exportSalesLeagueLeaderboardCsvApiArgSchema,
  GetSalesLeagueBranchBoardApiArg,
  GetSalesLeagueBranchBoardApiResponse,
  GetSalesLeagueLeaderboardApiArg,
  GetSalesLeagueLeaderboardApiResponse,
  getSalesLeagueBranchBoardApiArgSchema,
  getSalesLeagueLeaderboardApiArgSchema,
  salesLeagueBranchBoardResponseSchema,
  salesLeagueLeaderboardResponseSchema,
  SalesLeagueCsvExport,
} from './types';

/**
 * `fetchBaseQuery` has no built-in blob handler — only `'json'`, `'text'` or a function — so
 * reading a file needs one of these at all.
 *
 * Body and file name are read together here, in the one place that holds the `Response`,
 * rather than split across a `transformResponse`: that split can only reach the body as the
 * base query's `unknown`, and narrowing it back to `Blob` is an assertion nothing checks.
 *
 * The blob becomes an object URL immediately and never reaches the store as a `Blob` — see
 * `SalesLeagueCsvExport`. The name comes off the response rather than being invented, since
 * the backend builds it from the period, the branch and the window's closing date.
 *
 * The handler also runs on failures, so reading every response as a file would hand
 * `handleApiError` a Blob where it expects `{errors: [{code, message}]}` and turn a
 * documented 403 into a generic toast. The export shares the read endpoint's status codes,
 * so branching here is what lets a viewer outside a branch's scope be told exactly that.
 */
const readCsvExportResponse = async (response: Response): Promise<unknown> => {
  if (!response.ok) {
    // Left unnarrowed: the shape is the shared API error body, and `getApiErrorMessages`
    // is the one place that reads it.
    const errorBody: unknown = await response.json();

    return errorBody;
  }

  const csvExport: SalesLeagueCsvExport = {
    fileUrl: URL.createObjectURL(await response.blob()),
    fileName: getContentDispositionFileName(response.headers.get('content-disposition')),
  };

  return csvExport;
};

const salesLeagueApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getSalesLeagueLeaderboard: build.query<
      GetSalesLeagueLeaderboardApiResponse,
      GetSalesLeagueLeaderboardApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales-league/leaderboard`,
        params: {period: queryArg.period, branchId: queryArg.branchId},
      }),
      // The tag id carries the branch as well as the period: two branches share a period,
      // so a period-only id would let one board's invalidation clear another's.
      providesTags: (result, error, queryArg) => [
        {type: 'SalesLeagueLeaderboard', id: 'LIST'},
        {type: 'SalesLeagueLeaderboard', id: `${queryArg.branchId}-${queryArg.period}`},
      ],
      extraOptions: {
        requestSchema: getSalesLeagueLeaderboardApiArgSchema,
        responseSchema: salesLeagueLeaderboardResponseSchema,
      },
    }),

    getSalesLeagueBranchBoard: build.query<
      GetSalesLeagueBranchBoardApiResponse,
      GetSalesLeagueBranchBoardApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales-league/branch-board`,
        params: {period: queryArg.period},
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'SalesLeagueBranchBoard', id: 'LIST'},
        {type: 'SalesLeagueBranchBoard', id: `${queryArg.activeBranchId}-${queryArg.period}`},
      ],
      extraOptions: {
        requestSchema: getSalesLeagueBranchBoardApiArgSchema,
        responseSchema: salesLeagueBranchBoardResponseSchema,
      },
    }),

    /*
      Both exports are queries rather than mutations — they read a board and change nothing —
      but they are triggered by a button, so callers use the lazy hook. Neither provides a
      tag: there is nothing to invalidate, and `keepUnusedDataFor: 0` drops the entry as soon
      as the download is handed over rather than holding an object URL the caller has already
      revoked. No `responseSchema`, because the body is a file and not JSON.
    */
    exportSalesLeagueLeaderboardCsv: build.query<
      ExportSalesLeagueLeaderboardCsvApiResponse,
      ExportSalesLeagueLeaderboardCsvApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales-league/leaderboard/export/csv`,
        params: {period: queryArg.period, branchId: queryArg.branchId},
        responseHandler: readCsvExportResponse,
        cache: 'no-cache',
      }),
      keepUnusedDataFor: 0,
      extraOptions: {
        requestSchema: exportSalesLeagueLeaderboardCsvApiArgSchema,
      },
    }),

    exportSalesLeagueBranchBoardCsv: build.query<
      ExportSalesLeagueBranchBoardCsvApiResponse,
      ExportSalesLeagueBranchBoardCsvApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales-league/branch-board/export/csv`,
        params: {period: queryArg.period},
        responseHandler: readCsvExportResponse,
        cache: 'no-cache',
      }),
      keepUnusedDataFor: 0,
      extraOptions: {
        requestSchema: exportSalesLeagueBranchBoardCsvApiArgSchema,
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSalesLeagueLeaderboardQuery,
  useGetSalesLeagueBranchBoardQuery,
  useLazyExportSalesLeagueLeaderboardCsvQuery,
  useLazyExportSalesLeagueBranchBoardCsvQuery,
} = salesLeagueApi;
